import dayjs from '@dayjs';
import { forEach, intersection, isFinite, isNumber, keys, memoize } from 'lodash';
import millify from 'millify';
import { PERM_GROWZ_MICROGOALS_VIEW, PERM_MICROGOALS_LAUNCH } from '../config/permissions';
import urlAlias from '../config/urlAlias';
import store from '../redux/store';

export const campaignTypeToShort = {
  sponsoredProducts: 'sp',
  sponsoredDisplay: 'sd',
  sponsoredBrands: 'sb',
};

export const getUserState = memoize(() => {
  const { countryCode, currency, dateFormat, user, store: userStore, org, timezone } = store.getState().auth;
  return {
    countryCode,
    currency,
    dateFormat,
    user,
    store: userStore,
    org,
    timezone,
  };
});

// get url by alias
export const _route = (name, mappings = {}, params) => {
  let url = urlAlias[name];
  if (!url) return false;

  forEach(keys(mappings), (key) => {
    url = url.replace(`:${key}`, mappings[key]);
  });

  if (params) {
    return `${url}?${params}`;
  }

  return url;
};

// check if user has permissions
export const _can = (can) => {
  const { user, org } = getUserState();
  if (!user?._id) return false;
  // TODO: remove this check
  if (org.isPLGActive && [PERM_GROWZ_MICROGOALS_VIEW, PERM_MICROGOALS_LAUNCH].includes(can)) {
    return false;
  }
  if (user.rootUser) return true;

  const { ability } = user;
  const permissions = can.split('|');

  return intersection(ability, permissions).length > 0;
};

export const _int = (number, alt = 0) => {
  const { countryCode } = getUserState();
  const int = Number(number);
  if (!(isNumber(int) && isFinite(int) && !Number.isNaN(int))) return alt;
  const config = { maximumFractionDigits: 0 };
  return new Intl.NumberFormat(`en-${countryCode}`, config).format(int);
};

export const _number = (number, alt = 0) => {
  const { countryCode } = getUserState();
  const int = Number(number);
  if (!(isNumber(int) && isFinite(int) && !Number.isNaN(int))) return alt;
  const config = { maximumFractionDigits: 2 };
  return new Intl.NumberFormat(`en-${countryCode}`, config).format(int);
};

export const _percent = (number, alt = 0, decimal = 2) => {
  const { countryCode } = getUserState();
  const int = Number(number);
  if (!(isNumber(int) && isFinite(int) && !Number.isNaN(int))) return alt;
  const config = { maximumFractionDigits: decimal };
  const percentAge = new Intl.NumberFormat(`en-${countryCode}`, config).format(int);
  return `${percentAge}%`;
};

export const _date = (date, alt = '', format = null) => {
  const obj = dayjs(date);
  if (!date || !obj.isValid()) return alt;
  if (format) return obj.format(format);
  const { countryCode } = getUserState();
  return new Intl.DateTimeFormat(`en-${countryCode}`, { dateStyle: 'medium' }).format(obj);
};

export const _time = (date, alt = '', format = null) => {
  const obj = dayjs(date);
  if (!date || !obj.isValid()) return alt;
  if (format) return obj.format(format);
  const { countryCode } = getUserState();
  return new Intl.DateTimeFormat(`en-${countryCode}`, { timeStyle: 'short' }).format(obj);
};

export const _datetime = (date, alt = '', format = null) => {
  const obj = dayjs(date);
  if (!date || !obj.isValid()) return alt;
  if (format) return obj.format(format);
  const { countryCode } = getUserState();
  return new Intl.DateTimeFormat(`en-${countryCode}`, { dateStyle: 'medium', timeStyle: 'short' }).format(obj);
};

export const _dateago = (date, alt = '') => (date ? dayjs(date).fromNow() : alt);

export const _udate = (date, alt = '', format = null) => {
  if (isNumber(date) && String(date).length === 10) return _date(date * 1000, alt, format);
  return _date(date, alt, format);
};

export const _utime = (date, alt = '', format = null) => {
  if (isNumber(date) && String(date).length === 10) return _time(Number(date) * 1000, alt, format);
  return _time(Number(date), alt, format);
};

export const _udatetime = (date, alt = '', format = null) => {
  if (isNumber(date) && String(date).length === 10) return _datetime(Number(date) * 1000, alt, format);
  return _datetime(Number(date), alt, format);
};

export const _udateago = (date, alt = '') => {
  if (isNumber(date) && String(date).length === 10) return _dateago(Number(date) * 1000, alt);
  return _dateago(Number(date), alt);
};

export const _currency = (amount, alt = 0) => {
  const { countryCode, currency } = getUserState();
  const config = { style: 'currency', currency, maximumSignificantDigits: 4 };
  let number = Number(amount);
  if (!number || Number.isNaN(number) || !isFinite(number)) {
    if (!isNumber(alt)) {
      return alt;
    }
    number = 0;
  }
  return new Intl.NumberFormat(`en-${countryCode}`, config).format(number);
};

export const _kFormatter = (amount, alt = 0) => {
  let int = Number(amount);
  if (!int || Number.isNaN(int) || !isFinite(int)) {
    if (!isNumber(alt)) {
      return alt;
    }
    int = 0;
  }
  return millify(int, {
    precision: 2,
    decimalSeparator: '.',
  });
};

export const _unCamelCase = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
    .replace(/^./, (i) => i.toUpperCase());
};

export const workingDaysBetweenDates = (startDate, endDate, daysOnly = false) => {
  startDate = new Date(startDate);
  endDate = new Date(endDate);

  if (endDate < startDate) return 0;

  let millisecondsPerDay = 86400 * 1000;
  startDate.setHours(0, 0, 0, 1);
  endDate.setHours(23, 59, 59, 999);
  let diff = endDate - startDate;
  let days = Math.ceil(diff / millisecondsPerDay);

  if (daysOnly) {
    return days;
  }

  if (days > 1) {
    return `last ${days} days`;
  } else {
    return 'Yesterday';
  }
};

export const _diffDateAndToString = (startDate, endDate) => {
  const Difference_In_Time = endDate.getTime() - startDate.getTime();

  const Days = Difference_In_Time / (1000 * 3600 * 24);
  const Mins = Difference_In_Time / (60 * 1000);
  const Hours = Mins / 60;

  const diffDate = new Date(Difference_In_Time);
  var result = '';

  if (Mins < 60) {
    result = Mins + 'm';
  } else if (Hours < 24) result = diffDate.getMinutes() + 'h';
  else result = Days + 'd';

  return { result, Days, Mins, Hours };
};

export const _checkSubsetArray = (parentArray, subsetArray) => {
  return subsetArray.every((el) => {
    return parentArray.includes(el);
  });
};

export const _currencySymbol = (currency = null) => {
  const { currency: DefaultCurrency } = getUserState();
  const value = currency ?? DefaultCurrency;
  switch (value) {
    case 'MXN':
    case 'USD':
      return '$ ';
    case 'CAD':
    case 'SGD':
    case 'AUD':
      return '$ ';
    case 'BRL':
      return 'R$ ';
    case 'JPY':
      return '¥ ';
    case 'GBP':
      return '£ ';
    case 'EUR':
      return '€ ';
    case 'SAR':
      return '﷼ ';
    case 'AED':
      return 'د.إ ';
    case 'INR':
    default:
      return '₹ ';
  }
};

export const getMetricsKeys = (row = {}) => {
  return {
    cost: row.cost || 0,
    sales: row.sales || 0,
    orders: row.orders || 0,
    ctr: row.ctr || 0,
    clicks: row.clicks || 0,
    impressions: row.impressions || 0,
    roas: row.roas || 0,
    acos: row.acos || 0,
  };
};
